/**
 * Provider centralizado para gerenciamento de confirmações
 * 
 * Similar ao NotificationProvider, este provider gerencia confirmações
 * modais de forma centralizada, permitindo que qualquer parte da aplicação
 * solicite confirmações do usuário sem gerenciar estado local do modal.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { setGlobalConfirm, clearGlobalConfirm } from '../../lib/globalConfirm';
import { logger } from '../../lib/terminalLogger';

export interface ConfirmationOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface PendingConfirmation extends ConfirmationOptions {
  id: string;
  resolve: (confirmed: boolean) => void;
}

interface ConfirmationContextValue {
  pendingConfirmation: PendingConfirmation | null;
  confirm: (options: ConfirmationOptions) => Promise<boolean>;
  resolveConfirmation: (confirmed: boolean) => void;
}

const ConfirmationContext = createContext<ConfirmationContextValue>({} as ConfirmationContextValue);

export const useConfirmations = () => useContext(ConfirmationContext);

function generateId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function ConfirmationProvider({ children }: { children: React.ReactNode }) {
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [queue, setQueue] = useState<PendingConfirmation[]>([]);

  /**
   * Solicita uma confirmação do usuário
   * Retorna Promise<boolean> que resolve com true se confirmado, false se cancelado
   */
  const confirm = useCallback((options: ConfirmationOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      const id = generateId();
      const confirmation: PendingConfirmation = {
        ...options,
        id,
        resolve,
      };

      // Se já existe uma confirmação pendente, adicionar à fila
      if (pendingConfirmation) {
        setQueue((prev) => [...prev, confirmation]);
      } else {
        setPendingConfirmation(confirmation);
      }
    });
  }, [pendingConfirmation, queue.length]);

  // Inicializar função global para uso fora de componentes React
  useEffect(() => {
    setGlobalConfirm(confirm);
    return () => clearGlobalConfirm();
  }, [confirm]);

  /**
   * Resolve a confirmação atual e processa a próxima da fila, se houver
   */
  const resolveConfirmation = useCallback((confirmed: boolean) => {
    if (pendingConfirmation) {
      // Resolver a promise
      try {
        pendingConfirmation.resolve(confirmed);
      } catch (error) {
        logger.error('[CONFIRMATION_PROVIDER] Erro ao resolver confirmação:', error);
      }

      // Processar próxima confirmação da fila
      setQueue((currentQueue) => {
        const [next, ...rest] = currentQueue;
        if (next) {
          setPendingConfirmation(next);
          return rest;
        } else {
          setPendingConfirmation(null);
          return [];
        }
      });
    }
  }, [pendingConfirmation]);

  const value = useMemo(
    () => ({ pendingConfirmation, confirm, resolveConfirmation }),
    [pendingConfirmation, confirm, resolveConfirmation]
  );

  return (
    <ConfirmationContext.Provider value={value}>
      {children}
    </ConfirmationContext.Provider>
  );
}

/**
 * Hook de atalho que retorna apenas a função confirm
 */
export function useConfirm() {
  const { confirm } = useConfirmations();
  return confirm;
}

